<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { ArrowLeft, FolderOpen, Handshake, Pencil, UserRound } from '@lucide/vue';
import { useAuthStore } from '@/stores/auth';
import { useInvalidation } from '@/composables/useInvalidation';
import { useActiveTab } from '@/composables/useActiveTab';
import { useActiveTabScroll } from '@/composables/useActiveTabScroll';
import StatoErrore from '@/components/ui/StatoErrore.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import type { Partner } from '@evolfin/shared';
import FormPartner from '../components/FormPartner.vue';
import AnagraficaScheda from '../components/AnagraficaScheda.vue';
import PersoneSezione from '../components/PersoneSezione.vue';
import ProgettiSezione from '@/features/progetti/components/ProgettiSezione.vue';
import { dettaglioPartner } from '../api';

definePage({ meta: { titolo: 'Partner' } });

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const invalidate = useInvalidation('partner');

const partnerId = computed(() => Number((route.params as { id: string }).id));
const puoModificare = computed(() => auth.ha('can_write_partner'));

const {
  data: partner,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: ['partner', partnerId],
  queryFn: () => dettaglioPartner(partnerId.value),
  // È la pagina su cui si lavora: rimontandola si riparte dal server, non da
  // una copia di trenta secondi fa.
  refetchOnMount: 'always',
});

const tabs = [
  { chiave: 'anagrafica', etichetta: 'Anagrafica', icona: Handshake },
  { chiave: 'persone', etichetta: 'Persone', icona: UserRound },
  { chiave: 'progetti', etichetta: 'Progetti', icona: FolderOpen },
] as const;
type ChiaveTab = (typeof tabs)[number]['chiave'];
const tab = useActiveTab(
  tabs.map((t) => t.chiave),
  'anagrafica',
);
useActiveTabScroll(tab);

// Modifica anagrafica in-place sulla scheda (niente modale), come per Clienti.
const modifica = ref(false);
function avviaModifica(): void {
  tab.value = 'anagrafica';
  modifica.value = true;
}
function selezionaTab(chiave: ChiaveTab): void {
  modifica.value = false;
  tab.value = chiave;
}
function fineModifica(_partner: Partner): void {
  modifica.value = false;
  void invalidate();
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      type="button"
      class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
      @click="router.push('/partner')"
    >
      <ArrowLeft :size="15" :stroke-width="1.9" /> Partner
    </button>

    <div v-if="isLoading" class="text-[13px] text-ink-muted">Caricamento…</div>

    <StatoErrore
      v-else-if="isError || !partner"
      titolo="Partner non trovato"
      messaggio="Il partner richiesto non esiste o non è più disponibile."
      @riprova="refetch()"
    />

    <template v-else>
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
            aria-hidden="true"
          >
            <Handshake :size="24" :stroke-width="1.8" />
          </span>
          <div>
            <div class="flex flex-wrap items-center gap-2.5">
              <h1 class="font-display text-[28px] leading-tight font-semibold break-words">
                {{ partner.ragioneSociale }}
              </h1>
              <span
                class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
                :style="{
                  background: 'color-mix(in oklch, var(--color-info) 12%, transparent)',
                  color: 'var(--color-info)',
                }"
              >
                {{ partner.tipo }}
              </span>
            </div>
            <p class="mt-0.5 text-[13px] text-ink-secondary tabular-nums">
              {{ partner.codiceFiscale }}
            </p>
          </div>
        </div>
        <PulsanteAzione
          v-if="puoModificare && !modifica"
          variante="contorno"
          @click="avviaModifica"
        >
          Modifica
          <template #icona><Pencil :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
      </header>

      <nav
        ref="tabBar"
        class="flex gap-1 overflow-x-auto border-b border-line-soft"
        aria-label="Sezioni partner"
      >
        <button
          v-for="t in tabs"
          :key="t.chiave"
          type="button"
          class="flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
          :aria-current="tab === t.chiave ? 'true' : undefined"
          :class="
            tab === t.chiave
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-ink-secondary hover:text-ink'
          "
          @click="selezionaTab(t.chiave)"
        >
          <component :is="t.icona" :size="15" :stroke-width="1.8" />
          {{ t.etichetta }}
        </button>
      </nav>

      <section>
        <template v-if="tab === 'anagrafica'">
          <FormPartner
            v-if="modifica"
            :partner="partner"
            @salvato="fineModifica"
            @annulla="modifica = false"
          />
          <AnagraficaScheda v-else :partner="partner" />
        </template>
        <PersoneSezione v-else-if="tab === 'persone'" :partner-id="partner.id" />

        <ProgettiSezione v-else-if="tab === 'progetti'" :partner-id="partner.id" />
      </section>
    </template>
  </div>
</template>
